/**
 * Work Item Row - Compact row with action buttons
 *
 * Displays a single work item in a compact row format with:
 * - Priority indicator (colored left border)
 * - Title/identifier
 * - Due date with urgency styling
 * - Type-specific action buttons
 * - Done button (always available)
 */

import React, { useState, useMemo } from 'react';
import { currentLocale } from '../../i18n/format.js';
import {
  CheckCircle,
  Loader2,
  Edit,
  Mail,
  Upload,
  Eye,
  Phone,
  ArrowRight,
  UserPlus,
  Users,
  FileText,
  AlertCircle,
  RefreshCw,
  Car,
  Shield,
  Send,
  Calendar,
  Folder,
  Wrench,
  Clipboard,
  Hash,
  Gauge,
  CreditCard,
  Euro,
  User,
} from 'lucide-react';
import {
  WORK_ITEM_TYPES,
  PRIORITY_COLORS,
  PRIORITY_TEXT_COLORS,
  getTypeConfig
} from './pipelineConfig';

/**
 * Icon mapping for dynamic suggested_actions
 */
const ACTION_ICONS = {
  // PascalCase (legacy)
  Edit: Edit, Mail: Mail, Upload: Upload, Eye: Eye, Phone: Phone,
  ArrowRight: ArrowRight, UserPlus: UserPlus, FileText: FileText,
  AlertCircle: AlertCircle, RefreshCw: RefreshCw, CheckCircle: CheckCircle,
  // lowercase (from pipeline rule engine)
  edit: Edit, mail: Mail, upload: Upload, eye: Eye, phone: Phone,
  send: Send, car: Car, shield: Shield, calendar: Calendar,
  folder: Folder, wrench: Wrench, clipboard: Clipboard, hash: Hash,
  gauge: Gauge, euro: Euro, user: User, users: Users,
  'user-plus': UserPlus, 'file-text': FileText, 'id-card': CreditCard,
  'arrow-right': ArrowRight, 'alert-circle': AlertCircle,
};

/**
 * Color mapping for action types
 */
const ACTION_COLORS = {
  navigate: 'text-blue-600 hover:bg-blue-50',
  email_generate: 'text-amber-600 hover:bg-amber-50',
  document_upload: 'text-purple-600 hover:bg-purple-50',
  form_complete: 'text-green-600 hover:bg-green-50',
  workflow_advance: 'text-emerald-600 hover:bg-emerald-50',
  assign: 'text-indigo-600 hover:bg-indigo-50',
  call: 'text-green-600 hover:bg-green-50'
};

/**
 * Format due date with urgency indication
 */
function formatDueDate(dateStr) {
  if (!dateStr) return null;

  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      text: `${Math.abs(diffDays)}j en retard`,
      urgent: true
    };
  }
  if (diffDays === 0) {
    return {
      text: "Aujourd'hui",
      urgent: true
    };
  }
  if (diffDays === 1) {
    return {
      text: 'Demain',
      urgent: false
    };
  }
  if (diffDays <= 7) {
    return {
      text: `Dans ${diffDays}j`,
      urgent: false
    };
  }
  return {
    text: date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' }),
    urgent: false
  };
}

/**
 * Due Date Badge component
 */
function DueDateBadge({ date }) {
  const dueInfo = formatDueDate(date);

  if (!dueInfo) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  return (
    <span
      className={`text-sm whitespace-nowrap ${
        dueInfo.urgent
          ? 'text-red-600 font-medium'
          : 'text-gray-500'
      }`}
    >
      {dueInfo.text}
    </span>
  );
}

/**
 * Action Button component (for static type-based actions)
 */
function ActionButton({ action, onClick, loading, disabled }) {
  const Icon = action.icon;

  // Color mapping
  const colorClasses = {
    blue: 'text-blue-600 hover:bg-blue-50',
    amber: 'text-amber-600 hover:bg-amber-50',
    green: 'text-green-600 hover:bg-green-50',
    purple: 'text-purple-600 hover:bg-purple-50',
    gray: 'text-gray-600 hover:bg-gray-100'
  };

  const colorClass = colorClasses[action.color] || colorClasses.gray;

  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`p-1.5 rounded transition-colors ${colorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
      title={action.label}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" />
      )}
    </button>
  );
}

/**
 * Suggested Action Button component (for dynamic rule-generated actions)
 */
function SuggestedActionButton({ suggestedAction, onClick, loading, disabled }) {
  const Icon = ACTION_ICONS[suggestedAction.icon] || Edit;
  const colorClass = ACTION_COLORS[suggestedAction.action] || 'text-gray-600 hover:bg-gray-100';

  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`p-1.5 rounded transition-colors ${colorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
      title={suggestedAction.label}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" />
      )}
    </button>
  );
}

/**
 * WorkItemRow component
 */
function WorkItemRow({ item, onAction, onNavigate, onEmail, onSuggestedAction }) {
  const [actionLoading, setActionLoading] = useState(null);

  const typeConfig = getTypeConfig(item.type);
  const actions = typeConfig.actions || [];
  const priorityColor = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.P2;
  const priorityTextColor = PRIORITY_TEXT_COLORS[item.priority] || PRIORITY_TEXT_COLORS.P2;

  // Parse suggested_actions from item (stored as JSON string)
  const suggestedActions = useMemo(() => {
    if (!item.suggested_actions) return [];
    try {
      const parsed = typeof item.suggested_actions === 'string'
        ? JSON.parse(item.suggested_actions)
        : item.suggested_actions;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Failed to parse suggested_actions:', e);
      return [];
    }
  }, [item.suggested_actions]);

  // Handle suggested action click
  const handleSuggestedActionClick = async (suggestedAction) => {
    const actionKey = `suggested_${suggestedAction.action}_${suggestedAction.entityId || ''}`;
    setActionLoading(actionKey);

    try {
      switch (suggestedAction.action) {
        case 'navigate':
          // Navigate to entity detail (fall back to work item's entity if action doesn't specify)
          if (onNavigate) {
            onNavigate(
              suggestedAction.entity || item.entity_kind,
              suggestedAction.entityId || item.entity_id
            );
          }
          break;

        case 'email_generate':
          // Generate and download .eml file using existing email engine
          if (onEmail) {
            await onEmail(
              { entity_kind: suggestedAction.entity, entity_id: suggestedAction.entityId, id: item.id },
              suggestedAction.template
            );
          }
          break;

        case 'form_complete':
          if (onSuggestedAction) {
            onSuggestedAction(item, suggestedAction);
          } else if (onNavigate) {
            onNavigate(suggestedAction.entity, suggestedAction.entityId);
          }
          break;

        case 'document_upload':
          if (onSuggestedAction) {
            onSuggestedAction(item, suggestedAction);
          } else if (onNavigate) {
            onNavigate(suggestedAction.entity, suggestedAction.entityId);
          }
          break;

        case 'workflow_advance':
          // Execute workflow transition
          if (onAction) {
            await onAction(item.id, suggestedAction.workflowAction || 'DONE');
          }
          break;

        case 'assign':
          if (onSuggestedAction) {
            onSuggestedAction(item, suggestedAction);
          } else if (onNavigate) {
            onNavigate(suggestedAction.entity, suggestedAction.entityId);
          }
          break;

        case 'call':
          // Open phone dialer (if phone number available in params)
          if (suggestedAction.params?.phone) {
            window.location.href = `tel:${suggestedAction.params.phone}`;
          } else if (onNavigate) {
            onNavigate(suggestedAction.entity, suggestedAction.entityId);
          }
          break;

        default:
          console.warn('Unknown suggested action type:', suggestedAction.action);
      }
    } catch (error) {
      console.error('Suggested action failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle action click
  const handleActionClick = async (action) => {
    setActionLoading(action.key);

    try {
      switch (action.type) {
        case 'email':
          // Generate and download .eml file
          if (onEmail) {
            await onEmail(item, action.template);
          }
          break;

        case 'workflow':
          // Execute workflow transition
          if (onAction) {
            await onAction(item.id, action.action);
          }
          break;

        case 'navigate':
          // Navigate to entity detail
          if (onNavigate) {
            onNavigate(item.entity_kind, item.entity_id);
          }
          break;

        case 'action':
          // Generic action (like DONE)
          if (onAction) {
            await onAction(item.id, action.action);
          }
          break;

        default:
          console.warn('Unknown action type:', action.type);
      }
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle done click
  const handleDone = async () => {
    setActionLoading('done');
    try {
      if (onAction) {
        await onAction(item.id, 'DONE');
      }
    } catch (error) {
      console.error('Mark done failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
      {/* Priority indicator */}
      <div className={`w-1 h-10 rounded-full ${priorityColor}`} />

      {/* Title and type */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate text-sm">
          {item.title}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {typeConfig.label}
          {item.priority && (
            <span className={`ml-2 ${priorityTextColor}`}>
              {item.priority}
            </span>
          )}
        </p>
      </div>

      {/* Due date */}
      <div className="w-24 text-right">
        <DueDateBadge date={item.due_date} />
      </div>

      {/* Actions container */}
      <div className="flex items-center gap-1">
        {/* Suggested actions (dynamic, from pipeline rules) */}
        {suggestedActions.length > 0 && (
          <>
            {suggestedActions.map((suggestedAction, idx) => {
              const actionKey = `suggested_${suggestedAction.action}_${suggestedAction.entityId || ''}_${idx}`;
              return (
                <SuggestedActionButton
                  key={actionKey}
                  suggestedAction={suggestedAction}
                  onClick={() => handleSuggestedActionClick(suggestedAction)}
                  loading={actionLoading === actionKey.replace(`_${idx}`, '')}
                  disabled={actionLoading !== null}
                />
              );
            })}
            {/* Separator between suggested and type-specific actions */}
            {actions.length > 0 && (
              <div className="w-px h-5 bg-gray-200 mx-1" />
            )}
          </>
        )}

        {/* Type-specific actions (static, from pipelineConfig) */}
        {actions.map(action => (
          <ActionButton
            key={action.key}
            action={action}
            onClick={() => handleActionClick(action)}
            loading={actionLoading === action.key}
            disabled={actionLoading !== null}
          />
        ))}

        {/* Separator before Done button */}
        {(suggestedActions.length > 0 || actions.length > 0) && (
          <div className="w-px h-5 bg-gray-200 mx-1" />
        )}

        {/* Done button (always available) */}
        <button
          onClick={handleDone}
          disabled={actionLoading !== null}
          className="p-1.5 rounded text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Marquer termine"
        >
          {actionLoading === 'done' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default WorkItemRow;
